import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BBSLogo from "@/components/BBSLogo";
import AssetIQLogo from "@/components/AssetIQLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, Shield, MapPin, HardHat, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Pick up suspension / forced-logout reason set by the axios interceptor.
    try {
      const reason = localStorage.getItem("assetiq_logout_reason");
      if (reason) {
        setError(reason);
        localStorage.removeItem("assetiq_logout_reason");
      }
    } catch { /* ignore */ }
  }, []);

  if (user) {
    const dest = (user.role === "gate_keeper" || user.role === "logistics_supervisor") ? "/material-transfers" : "/";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      const dest = (res.user?.role === "gate_keeper" || res.user?.role === "logistics_supervisor") ? "/material-transfers" : "/";
      navigate(dest);
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left – Login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16">
        <div className="w-full max-w-md">
          <BBSLogo size={64} />
          <div className="mt-6">
            <AssetIQLogo height={48} />
          </div>
          <div className="mt-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fcf0f3] text-[#7C1D3A] text-xs font-semibold tracking-wider uppercase mb-4">
              <Shield className="w-3 h-3" />
              Secure Access
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
              Welcome back
            </h1>
            <p className="mt-2 text-slate-500 text-sm leading-relaxed">
              Internal fixed-asset management portal for <span className="font-semibold text-slate-700">BBS Global Pvt. Ltd.</span>
              &nbsp;Sign in with your company credentials to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Work Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                data-testid="login-email"
                placeholder="you@bbsglobal.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-medium text-[#7C1D3A] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required autoComplete="current-password"
                  data-testid="login-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  data-testid="toggle-password-visibility"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 focus:outline-none focus:text-[#7C1D3A]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full h-11 bg-[#7C1D3A] hover:bg-[#6c1c36] text-white font-medium"
            >
              {submitting ? "Signing in..." : "Sign In to Asset IQ"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right — Internal company panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#3b0b1a]">
        {/* Photo mosaic */}
        <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 gap-1 opacity-60">
          <div className="col-span-4 row-span-4 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/11861957/pexels-photo-11861957.jpeg?auto=compress&cs=tinysrgb&w=1200)" }} />
          <div className="col-span-2 row-span-2 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800)" }} />
          <div className="col-span-2 row-span-2 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/2760243/pexels-photo-2760243.jpeg?auto=compress&cs=tinysrgb&w=800)" }} />
          <div className="col-span-3 row-span-2 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/236722/pexels-photo-236722.jpeg?auto=compress&cs=tinysrgb&w=1000)" }} />
          <div className="col-span-3 row-span-2 bg-cover bg-center" style={{ backgroundImage: "url(https://images.pexels.com/photos/1108117/pexels-photo-1108117.jpeg?auto=compress&cs=tinysrgb&w=1000)" }} />
        </div>
        {/* Dark maroon overlay so text stays readable */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3b0b1a]/95 via-[#3b0b1a]/85 to-[#7C1D3A]/70" />
        {/* Subtle grid pattern for that industrial feel */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Top corner tag */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 bg-white/70" />
              <span className="font-heading text-xs tracking-[0.3em] uppercase font-bold">Internal Use · BBS Global</span>
            </div>
            <div className="text-right text-[10px] font-mono opacity-70 leading-tight">
              <div>{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}</div>
              <div>IST · Restricted Access</div>
            </div>
          </div>

          {/* Middle — internal-comms style headline */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/25 bg-black/20 backdrop-blur-sm text-[11px] font-semibold tracking-widest uppercase mb-6">
              <HardHat className="w-3.5 h-3.5" /> Plant Operations
            </div>
            <h2 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
              BBS Global's operational asset register.
            </h2>
            <p className="mt-5 text-white/80 text-base leading-relaxed">
              Machinery, spares, jigs and consumables across our plants and offices —
              maintained by our stores &amp; maintenance teams, verified physically each quarter.
            </p>

            {/* Internal chips instead of marketing stats */}
            <div className="mt-8 grid grid-cols-2 gap-2 max-w-md">
              {[
                { icon: MapPin, text: "Multi-plant · Multi-location" },
                { icon: HardHat, text: "Stores · Maintenance · Accounts" },
                { icon: Shield, text: "Role-scoped visibility" },
                { icon: Building2, text: "Registered under BBS Global Pvt. Ltd." },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-[12px] text-white/85 border border-white/15 bg-black/15 backdrop-blur-sm rounded-sm px-3 py-2">
                  <Icon className="w-3.5 h-3.5 shrink-0" /> {text}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom — footer */}
          <div className="flex items-end justify-end pt-6 border-t border-white/15">
            <div className="text-right font-mono text-[10px] text-white/60">
              © BBS Global Pvt. Ltd.<br />
              Asset IQ · Internal Use Only
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
